/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
import React, { useCallback, useState } from 'react';
import Checkbox from '@wconcept/wconcept-ui-renewal/components/atoms/Checkbox';
import Text from '@wconcept/wconcept-ui-renewal/components/atoms/Text';
import ModalTitle from '@wconcept/wconcept-ui-renewal/components/molecules/_title/ModalTitle';
import ModalGroupBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/ModalGroupBtn';
import ModalCloseBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/ModalCloseBtn';
import ModalEdgeCloseBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/ModalEdgeCloseBtn';
import AccordionPanel from '@wconcept/wconcept-ui-renewal/components/molecules/Accordion/AccordionPanel';
import AccrdnNoticeType from '@wconcept/wconcept-ui-renewal/components/organisms/_accordion/AccrdnNoticeType';
import * as S from './style';

interface IProps {
  onSubmit?: () => void;
  onClose?: () => void;
  value: {
    thirdPartyProvisionYn: string;
    marketingConsentYn: string;
    isMarketingAgreed: boolean;
    thirdPartyConentList: string[];
    marketingConsentType: string;
    isDesktop: boolean;
    isIPhoneXApp: boolean;
    /** 앱(WebView) 여부 — 웹에서는 'App Push' 채널 문구 미노출 */
    isNativeApp?: boolean;
    privacyNotice?: string[];
  };
}

const RaffleEventModal = ({ onSubmit, onClose, value }: IProps) => {
  const {
    thirdPartyProvisionYn,
    marketingConsentYn,
    isMarketingAgreed,
    thirdPartyConentList = [],
    marketingConsentType,
    isDesktop,
    isIPhoneXApp,
    isNativeApp = false,
    privacyNotice = { provider: '', purpose: '', items: '', period: '' },
  } = value;
  const [agreeThirdParty, setAgreeThirdParty] = useState(false); // 개인정보 제3자 제공 동의
  const [agreeMarketing, setAgreeMarketing] = useState(false); // 마케팅 활용/광고성 정보수신 동의
  const [agreePrivacy, setAgreePrivacy] = useState(false); // 개인정보 수집 및 이용 동의

  // 항목별 노출 여부 — 개인정보 수집(privacy)은 항상 노출되므로 최소 1개 보장
  const showThirdParty = thirdPartyProvisionYn === 'Y';
  const showMarketing = marketingConsentYn === 'Y' && !isMarketingAgreed;
  // 전체동의는 노출 항목이 2개 이상일 때만 표시
  const visibleCount = [showThirdParty, showMarketing, true].filter(
    Boolean
  ).length;
  const isShowAllAgree = visibleCount >= 2;
  // 노출된 항목만 기준으로 판단 (파생값 — 개별 체크가 모두 완료되면 전체동의도 자동 체크)
  const isAllAgree =
    (!showThirdParty || agreeThirdParty) &&
    (!showMarketing || agreeMarketing) &&
    agreePrivacy;
  const CAUTION_TEXT =
    '동의를 거부할 권리가 있으며, 거부하실 경우 이벤트 응모가 불가합니다.';

  const handleSubmit = useCallback(() => {
    if (typeof onSubmit === 'function') onSubmit();
  }, [onSubmit]);

  const handleClose = useCallback(() => {
    if (typeof onClose === 'function') onClose();
  }, [onClose]);

  return (
    <S.RaffleEventModal className={isIPhoneXApp ? 'is-old-app-iphonex' : ''}>
      {isDesktop ? (
        <ModalCloseBtn onClick={handleClose} />
      ) : (
        <ModalEdgeCloseBtn onClick={handleClose} />
      )}
      <ModalTitle title="필수 항목 동의 후 이벤트 참여하기" />
      <div className="content">
        {isShowAllAgree && (
          <div className="all-agree">
            <Checkbox
              type="type1"
              id="allAgree"
              label="전체 동의합니다."
              value="allAgree"
              checked={isAllAgree}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const { checked } = e.target;
                // 노출된 항목만 일괄 토글
                if (showThirdParty) setAgreeThirdParty(checked);
                if (showMarketing) setAgreeMarketing(checked);
                setAgreePrivacy(checked);
              }}
            />
            <Text>이벤트 참여를 위한 필수 동의 항목을 모두 포함합니다.</Text>
          </div>
        )}
        {showThirdParty && (
          <AccrdnNoticeType>
            <AccordionPanel
              label={
                <span onClick={e => e.stopPropagation()}>
                  <Checkbox
                    type="type1"
                    id="thirdPartyAgree"
                    label="[필수] 이벤트 응모를 위한 개인정보 제 3자 제공동의"
                    value="thirdPartyAgree"
                    checked={agreeThirdParty}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setAgreeThirdParty(e.target.checked)
                    }
                  />
                </span>
              }
              value="notice-1"
            >
              <div className="agree-notice">
                <ul className="agree-notice-list">
                  {thirdPartyConentList?.map((line, index) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>

                <Text className="caution-text">{CAUTION_TEXT}</Text>
              </div>
            </AccordionPanel>
          </AccrdnNoticeType>
        )}
        {showMarketing && (
          <AccrdnNoticeType>
            <AccordionPanel
              label={
                <span onClick={e => e.stopPropagation()}>
                  <Checkbox
                    type="type1"
                    id="marketingAgree"
                    label="[필수] 마케팅 활용을 위한 광고성 정보수신 및 개인정보 이용 동의"
                    value="marketingAgree"
                    checked={agreeMarketing}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setAgreeMarketing(e.target.checked)
                    }
                  />
                </span>
              }
              value="notice-2"
            >
              <div className="agree-notice">
                <table className="agree-notice-table">
                  <colgroup>
                    <col width="20%" />
                    <col width="30%" />
                    <col width="50%" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>구분</th>
                      <th>수집 항목</th>
                      <th>이용 목적</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>마케팅 활용</td>
                      <td>이메일 주소, 휴대폰 번호, 생년월일, 성별</td>
                      <td>
                        <b>
                          신규 서비스/상품안내 등 마케팅 활용 및 광고성 정보
                          전달
                        </b>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <dl className="agree-notice-desc">
                  <dt>보유 및 이용기간</dt>
                  <dd>
                    <b>회원탈퇴 시 또는 법적 의무 보유기간</b>
                  </dd>
                  <dt>수신 채널</dt>
                  {marketingConsentType === 'BASIC' && <dd>SMS, 이메일</dd>}
                  {marketingConsentType === 'PUSH' && (
                    <dd>
                      {isNativeApp ? 'SMS, 이메일, App Push' : 'SMS, 이메일'}
                    </dd>
                  )}
                </dl>
                <Text className="caution-text">{CAUTION_TEXT}</Text>
              </div>
            </AccordionPanel>
          </AccrdnNoticeType>
        )}
        <AccrdnNoticeType>
          <AccordionPanel
            label={
              <span onClick={e => e.stopPropagation()}>
                <Checkbox
                  type="type1"
                  id="privacyAgree"
                  label="[필수] 개인정보 수집 및 이용 동의"
                  value="privacyAgree"
                  checked={agreePrivacy}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setAgreePrivacy(e.target.checked)
                  }
                />
              </span>
            }
            value="notice-3"
          >
            <div className="agree-notice">
              <dl className="agree-notice-desc">
                <dt>수집 및 이용 목적</dt>
                <dd>이벤트 응모 확인</dd>
                <dt>수집 및 이용 항목</dt>
                <dd>
                  성명, ID, 휴대폰 번호,주소(당첨자 중 실물 배송이 있을 경우
                  해당)
                </dd>
                <dt>
                  <b>보유 및 이용기간</b>
                </dt>
                <dd>
                  <b>당첨 문자 발송 후 2개월간 보관</b>
                </dd>
              </dl>
              <Text className="caution-text">{CAUTION_TEXT}</Text>
            </div>
          </AccordionPanel>
        </AccrdnNoticeType>
      </div>
      <ModalGroupBtn
        type="rightBig"
        secondaryBtn={{ text: '취소', onClick: handleClose }}
        primaryBtn={{
          text: '동의하고 참여하기',
          onClick: handleSubmit,
          disabled: !isAllAgree,
        }}
      />
    </S.RaffleEventModal>
  );
};

export default RaffleEventModal;
